/** @format */

import {SearchIcon, XIcon} from '@heroicons/react/solid'
import clsx from 'clsx'
import React, {
  useEffect,
  useState,
  type ChangeEventHandler,
  type FormEvent,
  type ReactNode,
} from 'react'

import LoaderButton from '@src/components/tailwind/LoaderButton'
import * as Modal from '@src/components/tailwind/ModalV2'
import {UpgradeMessage} from '@src/components/tailwind/UpgradeMessage'

import {useSubmissionsContext} from './SubmissionsContext'

export function ActiveSearchBar() {
  // Track internal search state and only "commit" the change to context
  // which triggers a refetch on submit.
  const {search, setSearch} = useSubmissionsContext()
  const [state, setState] = useState(search)

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSearch(state)
  }

  function handleClearSearchClick() {
    setState('')
    setSearch('')
  }

  // when search changes, sync the local state to match
  useEffect(() => {
    setState(search)
  }, [search])

  return (
    <form onSubmit={handleSubmit}>
      <SearchBar
        disabled={!state.trim() || state === search}
        onChange={event => setState(event.target.value)}
        suffix={
          (state.trim() || search) && (
            <button
              className="shrink-0 rounded-full p-0.5 text-gray-500 hover:bg-gray-200"
              onClick={handleClearSearchClick}
              type="button"
            >
              <XIcon className="h-5 w-5" />
            </button>
          )
        }
        value={state}
      />
    </form>
  )
}

export function RequireUpgradeSearchBar() {
  const [isModalOpen, setModalOpen] = useState(false)
  return (
    <>
      <div className="relative">
        {/* invisible overlay to catch the click event */}
        <div
          className="absolute inset-0 z-10 cursor-pointer"
          onClick={() => setModalOpen(true)}
        />
        <SearchBar disabled />
      </div>

      <Modal.Root isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
        <Modal.DefaultLayout>
          <Modal.Header title="Submission Search Bar" />
          <Modal.Content>
            <UpgradeMessage alignButton="end" feature="search_submissions">
              The search bar allows you to search through your submission
              archive by keywords or phrases.
            </UpgradeMessage>
          </Modal.Content>
        </Modal.DefaultLayout>
      </Modal.Root>
    </>
  )
}

type SearchBarProps = {
  disabled?: boolean
  onChange?: ChangeEventHandler<HTMLInputElement>
  suffix?: ReactNode
  value?: string
}

function SearchBar(props: SearchBarProps) {
  const {disabled, suffix, ...inputProps} = props
  return (
    <div className="flex items-center gap-x-1">
      <div
        className={clsx(
          'flex grow items-center gap-x-2 rounded border-2 border-gray-100 bg-gray-100 px-2 focus:outline-none',
          !disabled && 'focus-within:ring-1 focus-within:ring-gray-400',
        )}
      >
        <SearchIcon className="h-6 w-6 shrink-0" />
        <input
          {...inputProps}
          className={clsx(
            'h-10 grow',
            // clear global style on input element from main.css
            'm-0 appearance-none border-none bg-transparent p-0 focus:ring-0',
          )}
          placeholder="Search submissions…"
        />
        {suffix}
      </div>
      <LoaderButton className="shrink-0" disabled={disabled}>
        Search
      </LoaderButton>
    </div>
  )
}
